import React from 'react';
import { Link } from 'react-router-dom';
import { Truck, Shield, Clock, Users, MapPin, Check, ArrowRight, HardHat, Leaf, Lightbulb, HelpCircle, ExternalLink, ChevronDown } from 'lucide-react';
import { contactInfo } from '../data/mock';

// Modification #17: Services sans doublons - BTP et Jardinage sont dans une section dédiée plus bas
// Modification #19: Titres des blocs remplacés  
// Modification #22: "Garantie Travaux" -> "Conseils et Formation à l'utilisation"
const services = [
  {
    icon: Truck,
    title: 'Livraison & Reprise',
    description: 'Nous proposons la livraison et la reprise du matériel pour vous simplifier la vie. Service disponible en Haute et Basse-Normandie.',
    features: ['Livraison sur site', 'Reprise du matériel', 'Tarif selon distance', 'Sur rendez-vous']
  },
  {
    icon: Lightbulb,
    title: 'Conseils et Formation à l\'utilisation',
    description: 'Nous vous accompagnons dans la prise en main du matériel loué. Conseils personnalisés et démonstrations pour une utilisation optimale et sécurisée.',
    features: ['Démonstration incluse', 'Conseils de sécurité', 'Mode d\'emploi fourni', 'Assistance téléphonique']
  }
];

// Modification #18: Processus de livraison déplacé depuis FAQ
const deliverySteps = [
  {
    title: 'Planification du rendez-vous',
    desc: 'Nous vous contactons 48h avant pour confirmer le créneau de livraison qui vous convient.'
  },
  {
    title: 'Livraison sur site',
    desc: 'Notre équipe livre le matériel à l\'adresse indiquée, en Haute et Basse-Normandie.'
  },
  {
    title: 'Vérification ensemble',
    desc: 'Nous vérifions ensemble le bon fonctionnement du matériel et répondons à vos questions.'
  },
  {
    title: 'Reprise en fin de location',
    desc: 'À la date convenue, nous reprenons le matériel. Assurez-vous qu\'il soit propre et accessible.'
  }
];

// Modification #24: FAQ section avec titre "Vous avez besoin d'un service ?"
const serviceFAQ = [
  {
    question: "Comment réserver du matériel ?",
    answer: "Vous pouvez réserver via notre formulaire de contact ou directement en ligne sur notre plateforme Lokki. Un acompte de 30% est requis pour confirmer la réservation."
  },
  {
    question: "Quels sont les délais de réservation ?",
    answer: "Nous recommandons de réserver au moins 2 semaines à l'avance, et plusieurs mois pour les périodes de forte demande (mai à septembre)."
  },
  {
    question: "Proposez-vous des tarifs professionnels ?",
    answer: "Oui, nous proposons des tarifs préférentiels pour les professionnels et les commandes régulières. Contactez-nous pour un devis personnalisé."
  }
];

const ServicesPage = () => {
  const [openFAQ, setOpenFAQ] = React.useState(0);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-20">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-4xl sm:text-5xl font-black text-white mb-6">
            Nos <span className="text-green-400">Services</span>
          </h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto">
            Location de matériel et prestations clé en main pour tous vos projets
          </p>
        </div>
      </section>

      {/* Services Grid - Modification #20: Dégradé de vert */}
      <section className="py-20 bg-gradient-to-b from-green-50 to-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <div 
                key={index}
                className="bg-white rounded-3xl p-8 shadow-sm hover:shadow-xl transition-all duration-300 border border-green-100"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center mb-6">
                  <service.icon className="text-white" size={32} />
                </div>
                <h3 className="text-2xl font-bold text-slate-800 mb-4">{service.title}</h3>
                <p className="text-slate-600 mb-6 leading-relaxed">{service.description}</p>
                <div className="grid grid-cols-2 gap-3">
                  {service.features.map((feature, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Check className="text-green-500 flex-shrink-0" size={18} />
                      <span className="text-sm text-slate-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          
          {/* Modification #21: Boutons alignés sur la même ligne */}
          <div className="mt-12 flex flex-wrap justify-center gap-4">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 bg-green-500 text-white font-bold rounded-xl hover:bg-green-600 transition-colors"
            >
              Demander un devis
              <ArrowRight size={20} />
            </Link>
            <a
              href="https://3dloc.lokki.rent/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-4 border-2 border-green-500 text-green-600 font-bold rounded-xl hover:bg-green-50 transition-colors"
            >
              Réserver en ligne
              <ExternalLink size={20} />
            </a>
          </div>
        </div>
      </section>

      {/* Modification #18: Processus de Livraison (déplacé depuis FAQ) */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-green-100 text-green-600 font-semibold rounded-full text-sm mb-4">
              Service Livraison
            </span>
            <h2 className="text-3xl sm:text-4xl font-black text-slate-800 mb-4">
              Processus de <span className="text-green-500">Livraison</span>
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              Nous livrons votre matériel directement sur votre lieu de projet.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6 mb-12">
            {deliverySteps.map((step, index) => (
              <div key={index} className="relative">
                <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-6 h-full border border-green-200">
                  <div className="w-10 h-10 bg-green-500 text-white rounded-full flex items-center justify-center font-bold mb-4">
                    {index + 1}
                  </div>
                  <h3 className="text-lg font-bold text-slate-800 mb-2">{step.title}</h3>
                  <p className="text-slate-600 text-sm">{step.desc}</p>
                </div>
                {index < 3 && (
                  <ArrowRight className="hidden md:block absolute top-1/2 -right-3 text-green-400 z-10" size={20} />
                )}
              </div>
            ))}
          </div>

          <div className="bg-green-50 border border-green-200 rounded-2xl p-6">
            <div className="grid md:grid-cols-3 gap-6 text-center">
              <div>
                <p className="text-lg font-black text-green-600">Haute & Basse-Normandie</p>
                <p className="text-slate-600">Zone de livraison</p>
              </div>
              <div>
                <p className="text-2xl font-black text-green-600">Sur devis</p>
                <p className="text-slate-600">Selon distance</p>
              </div>
              <div>
                <p className="text-2xl font-black text-green-600">7J/7</p>
                <p className="text-slate-600">Sur rendez-vous</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Modification #23: Bloc dédié Barnum Événement - fond blanc + couleur orange + IMAGE */}
      <section className="py-20 bg-white border-y border-orange-100">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Image à gauche */}
            <div className="relative order-2 lg:order-1">
              <div className="rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src="/media/couverture-chapiteau-plage-high-2a42f963.jpg"
                  alt="Barnum Événement - Chapiteaux et réceptions"
                  className="w-full h-80 lg:h-[450px] object-cover"
                />
              </div>
              {/* Badge flottant */}
              <div className="absolute -bottom-6 -right-6 bg-orange-500 text-white px-6 py-3 rounded-2xl shadow-xl font-bold">
                Installation incluse
              </div>
            </div>
            
            {/* Contenu à droite */}
            <div className="order-1 lg:order-2">
              <div className="inline-block px-4 py-2 bg-orange-100 text-orange-600 font-semibold rounded-full text-sm mb-4">
                Notre partenaire événementiel
              </div>
              <h2 className="text-3xl sm:text-4xl font-black text-slate-800 mb-6">
                Organisation d'<span className="text-orange-500">Événements</span>
              </h2>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                Pour l'organisation complète de vos événements (mariage, anniversaire, séminaire...), 
                découvrez notre partenaire <strong className="text-orange-600">Barnum Événement</strong> qui propose :
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  'Livraison et installation des chapiteaux',
                  'Montage et démontage par nos équipes',
                  'Conseils personnalisés pour votre événement',
                  'Mobilier, vaisselle, décoration, sono...'
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-slate-700">
                    <div className="w-6 h-6 bg-orange-100 rounded-full flex items-center justify-center">
                      <Check size={14} className="text-orange-600" />
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
              <a
                href="https://www.barnum-evenement.fr"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-4 bg-orange-500 text-white font-bold rounded-xl hover:bg-orange-600 transition-colors"
              >
                Découvrir Barnum Événement
                <ExternalLink size={20} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Réalisation Chantiers - Dégradé vert */}
      <section className="py-20 bg-gradient-to-b from-white to-green-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-green-100 text-green-600 font-semibold rounded-full text-sm mb-4">
              Nos Prestations
            </span>
            <h2 className="text-3xl sm:text-4xl font-black text-slate-800 mb-4">
              Réalisation de <span className="text-green-500">Chantiers</span>
            </h2>
            <p className="text-slate-600 text-lg max-w-3xl mx-auto">
              En plus de la location de matériel, nous réalisons vos chantiers BTP et jardinage clé en main.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* BTP */}
            <div className="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow border border-green-100">
              <div className="h-48 bg-gradient-to-br from-green-600 to-green-700 relative overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800&auto=format"
                  alt="Chantier BTP"
                  className="w-full h-full object-cover mix-blend-overlay opacity-60"
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                    <HardHat className="text-white" size={40} />
                  </div>
                </div>
              </div>
              <div className="p-8">
                <h3 className="text-2xl font-bold text-slate-800 mb-4">Chantiers BTP</h3>
                <p className="text-slate-600 mb-6 leading-relaxed">
                  Notre équipe qualifiée prend en charge tous types de travaux, du terrassement aux finitions.
                </p>
                <div className="flex flex-wrap gap-2 mb-6">
                  {['Terrassement', 'Maçonnerie', 'Démolition', 'Dalle béton', 'Fondations'].map((item, i) => (
                    <span key={i} className="px-3 py-1 bg-green-50 text-green-700 rounded-full text-sm font-medium">
                      {item}
                    </span>
                  ))}
                </div>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-green-600 text-white font-semibold rounded-xl hover:bg-green-700 transition-colors"
                >
                  Demander un devis BTP
                  <ArrowRight size={18} />
                </Link>
              </div>
            </div>

            {/* Jardinage */}
            <div className="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow border border-green-100">
              <div className="h-48 bg-gradient-to-br from-green-500 to-green-600 relative overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=800&auto=format"
                  alt="Jardinage"
                  className="w-full h-full object-cover mix-blend-overlay opacity-60"
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                    <Leaf className="text-white" size={40} />
                  </div>
                </div>
              </div>
              <div className="p-8">
                <h3 className="text-2xl font-bold text-slate-800 mb-4">Jardinage & Espaces Verts</h3>
                <p className="text-slate-600 mb-6 leading-relaxed">
                  Confiez-nous l'entretien et l'aménagement de vos espaces verts.
                </p>
                <div className="flex flex-wrap gap-2 mb-6">
                  {['Tonte', 'Taille haies', 'Élagage', 'Débroussaillage', 'Potager'].map((item, i) => (
                    <span key={i} className="px-3 py-1 bg-green-50 text-green-700 rounded-full text-sm font-medium">
                      {item}
                    </span>
                  ))}
                </div>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-green-600 text-white font-semibold rounded-xl hover:bg-green-700 transition-colors"
                >
                  Demander un devis Jardinage
                  <ArrowRight size={18} />
                </Link>
              </div>
            </div>
          </div>

          {/* Modification #22: "Garantie Travaux" -> "Conseils et Formation à l'utilisation" */}
          <div className="mt-12 bg-white rounded-2xl p-8 shadow-sm border border-green-100">
            <h3 className="text-xl font-bold text-slate-800 mb-6 text-center">Pourquoi nous choisir ?</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { title: 'Équipe qualifiée', desc: 'Professionnels expérimentés' },
                { title: 'Devis gratuit', desc: 'Estimation sans engagement' },
                { title: 'Matériel pro', desc: 'Équipements de qualité' },
                { title: 'Conseils et Formation', desc: 'Accompagnement personnalisé' }
              ].map((item, i) => (
                <div key={i} className="text-center">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                    <Check className="text-green-600" size={24} />
                  </div>
                  <h4 className="font-semibold text-slate-800 mb-1">{item.title}</h4>
                  <p className="text-sm text-slate-500">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Modification #24: Bloc FAQ avec titre "Vous avez besoin d'un service ?" */}
      <section className="py-20 bg-green-50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <HelpCircle className="text-green-600" size={32} />
            </div>
            <h2 className="text-3xl sm:text-4xl font-black text-slate-800 mb-4">
              Vous avez besoin d'un <span className="text-green-500">service</span> ?
            </h2>
            <p className="text-slate-600">
              Retrouvez les réponses aux questions les plus fréquentes
            </p>
          </div>

          <div className="space-y-4">
            {serviceFAQ.map((item, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl overflow-hidden shadow-sm border border-green-100"
              >
                <button
                  onClick={() => setOpenFAQ(openFAQ === index ? -1 : index)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-green-50 transition-colors"
                >
                  <span className="text-lg font-semibold text-slate-800 pr-4">
                    {item.question}
                  </span>
                  <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                    openFAQ === index 
                      ? 'bg-green-500 text-white rotate-180' 
                      : 'bg-green-100 text-green-600'
                  }`}>
                    <ChevronDown size={20} />
                  </div>
                </button>
                
                <div className={`overflow-hidden transition-all duration-300 ${
                  openFAQ === index ? 'max-h-96' : 'max-h-0'
                }`}>
                  <div className="px-6 pb-6 text-slate-600 leading-relaxed">
                    {item.answer}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 text-center">
            <Link
              to="/faq"
              className="inline-flex items-center gap-2 text-green-600 font-semibold hover:text-green-700"
            >
              Voir toutes les questions fréquentes
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-black text-slate-800 mb-6">
                Zone d'<span className="text-green-500">intervention</span>
              </h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                Notre dépôt est situé à Cauville-sur-Mer, à 15 minutes du Havre et d'Etretat.
                Nous intervenons dans tout le Pays de Caux et ses environs.
              </p>
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                    <MapPin className="text-green-600" size={24} />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-800">Cauville-sur-Mer</p>
                    <p className="text-slate-500">À 15min du Havre et Etretat</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                    <Users className="text-green-600" size={24} />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-800">Pro & Particuliers</p>
                    <p className="text-slate-500">Tarifs adaptés à chaque profil</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-3xl p-8 lg:p-12 text-white">
              <h3 className="text-2xl font-bold mb-6">Contactez-nous</h3>
              <div className="space-y-4 mb-8">
                <p>
                  <span className="font-semibold">Téléphone :</span> {contactInfo.phone}
                </p>
                <p>
                  <span className="font-semibold">Email :</span> {contactInfo.email}
                </p>
                <p>
                  <span className="font-semibold">Horaires :</span><br/>
                  {contactInfo.hours.weekdays}<br/>
                  {contactInfo.hours.saturday}<br/>
                  {contactInfo.hours.sunday}
                </p>
              </div>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-8 py-4 bg-white text-green-600 font-bold rounded-xl hover:bg-green-50 transition-colors"
              >
                Demander un devis
                <ArrowRight size={20} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
